<script>
  import { createEventDispatcher } from "svelte";
  import { draggable } from "$lib/utils/draggable";
  import { Icon, Folder, XMark } from "svelte-hero-icons";
  import { tooltip } from "$lib/utils/tooltip";

  export let value = [];
  export let isReadonly = false;
  export let label = "";
  export let multiple = false;
  export let optional = false;
  export let selectionTheme = null;
  export let store;

  if (!store) {
    console.error("No screen store provided.");
  }

  const screens = store;
  const dispatch = createEventDispatcher();
  const selectionThemes = {
    only: {
      accent: "#009E73",
      background: "#E0F2EC",
      text: "#00684A",
    },
    ignore: {
      accent: "#D55E00",
      background: "#FBEAE0",
      text: "#8A3D00",
    },
  };

  let orderedScreens = JSON.parse(JSON.stringify($screens));
  let selectedScreens = [];
  $: selectionColors = selectionThemes[selectionTheme] || null;

  $: selectedScreens = Array.isArray(value) ? value : [value];
  $: orderScreens(selectedScreens);

  function orderScreens(selectedScreens) {
    if (!multiple) return;

    orderedScreens = orderedScreens.sort((a, b) => {
      const indexA = selectedScreens.indexOf(a.name);
      const indexB = selectedScreens.indexOf(b.name);

      return (indexA === -1 ? Infinity : indexA) - (indexB === -1 ? Infinity : indexB);
    });
  }

  function toggleScreen(screen) {
    if (isReadonly) return;

    let newValue;
    /*
    TODO: We should switch to ids here
    if (multiple) {
      newValue = selectedScreens.includes(screen._id)
        ? selectedScreens.filter((id) => id !== screen._id)
        : [...selectedScreens, screen._id];
    } else {
      newValue = screen._id;
    }
    */

    if (multiple) {
      newValue = selectedScreens.includes(screen.name)
        ? selectedScreens.filter((name) => name !== screen.name)
        : [...selectedScreens, screen.name];
    } else {
      newValue = [screen.name];
    }

    dispatch("input", newValue);
  }

  function clearSelection() {
    if (isReadonly) return;
    dispatch("input", null);
  }

  function handleReorder(event) {
    const { from, to } = event.detail;

    const item = orderedScreens[from];
    orderedScreens.splice(from, 1);
    orderedScreens.splice(to, 0, item);
    orderedScreens = orderedScreens;

    const newValue = orderedScreens
      .filter((screen) => selectedScreens.includes(screen.name))
      .map((screen) => screen.name);

    dispatch("input", newValue);
  }

  function isDraggable(screen) {
    return multiple && !isReadonly && selectedScreens.includes(screen.name);
  }

  function selectedItemStyle(isSelected) {
    if (!isSelected || !selectionColors) return "";

    return `background-color: ${selectionColors.background}; border-color: ${selectionColors.accent}; color: ${selectionColors.text};`;
  }

  function selectedIconStyle(isSelected) {
    if (!isSelected || !selectionColors) return "";

    return `color: ${selectionColors.accent};`;
  }

  function inputStyle() {
    if (!selectionColors) return "";

    return `accent-color: ${selectionColors.accent};`;
  }
</script>

<!-- svelte-ignore a11y-label-has-associated-control -->
{#if label}<label class="block text-content-subtle text-xs mb-2">{label}</label>{/if}

<div class="border border-line-strong rounded-lg p-2 max-w-md" class:opacity-50={isReadonly}>
  <div class="flex justify-between items-center mb-1">
    <h3 class="font-semibold text-xs">Screens</h3>
    <div class="flex items-center">
      {#if optional && !multiple && selectedScreens.length > 0}
        <button
          class="text-xs text-content-subtle hover:text-content focus:outline-none"
          on:click={clearSelection}
          disabled={isReadonly}
        >
          <Icon src={XMark} class="w-4 h-4" />
        </button>
      {/if}
      <slot name="dynamic" />
    </div>
  </div>
  <ul use:draggable on:reorder={handleReorder} class="space-y-0.5 max-h-48 overflow-y-auto">
    {#each orderedScreens as screen (screen._id)}
      {@const isSelected = selectedScreens.includes(screen.name)}
      <li draggable={isDraggable(screen)}>
        <button
          class="w-full text-left flex items-center px-1.5 py-0.5 rounded-sm border border-transparent hover:bg-surface-hover focus:outline-none focus:bg-surface-muted text-xs transition-colors"
          class:cursor-move={isDraggable(screen)}
          style={selectedItemStyle(isSelected)}
          on:click={() => toggleScreen(screen)}
          use:tooltip={{ placement: "left" }}
          aria-label={screen.name || "Unnamed Screen"}
          disabled={isReadonly}
        >
          {#if multiple}
            <!--
            TODO: We should switch to ids here:
            checked={selectedScreens.includes(screen._id)}
            -->
            <input
              type="checkbox"
              checked={isSelected}
              class="mr-1.5 h-3 w-3 cursor-pointer"
              style={inputStyle()}
              on:change={() => {}}
              disabled={isReadonly}
            />
          {:else}
            <!--
            TODO: We should switch to ids here:
            checked={selectedScreens.includes(screen._id)}
            -->
            <input
              type="radio"
              checked={isSelected}
              class="mr-1.5 h-3 w-3"
              style={inputStyle()}
              on:change={() => {}}
              disabled={isReadonly}
            />
          {/if}
          <Icon
            src={Folder}
            class="w-3 h-3 text-content-muted mr-1 shrink-0"
            style={selectedIconStyle(isSelected)}
          />
          <span class="truncate">{screen.name || "Unnamed Screen"}</span>
        </button>
      </li>
    {/each}
  </ul>
</div>
